import {
  findSegmentField,
  SegmentDataStructureType,
} from "@redotech/redo-customer-segments-common/marketing/segments/segment-data-structures";
import { WhereConditionDataType } from "@redotech/redo-customer-segments-common/segments/segment-where-condition";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";
import { SegmentBuilderText } from "../segment-builder-text";
import { WhereConditionInProgress } from "../segment-in-progress";
import { AnnualDateTimeframeFilter } from "./annual-date-timeframe-filter";
import { BooleanFilter } from "./boolean-filter";
import { CityProximityFilter } from "./city-proximity-filter";
import { CustomFieldFilter } from "./custom-field-filter";
import { FullDateTimeframeFilter } from "./full-date-timeframe-filter";
import { NumericFilter } from "./numeric-filter";
import { StringFilter } from "./string-filter";
import { TokenFilter } from "./token-filter";
import { TokenHierarchyFilter } from "./token-hierarchy-filter";
import { TokenListFilter } from "./token-list-filter";

export const FilterConfig = memo(function FilterConfig({
  condition,
  setCondition,
  dataStructure,
}: {
  condition: WhereConditionInProgress<any>;
  setCondition: (condition: WhereConditionInProgress<any>) => void;
  dataStructure: SegmentDataStructureType;
}) {
  // Look up the field to get its source
  const field = findSegmentField(dataStructure, condition.dimension);
  const fieldSource = field?.source;

  switch (condition.type) {
    case WhereConditionDataType.TOKEN_LIST:
      return (
        <TokenListFilter
          comparison={condition.comparison}
          dataStructure={dataStructure}
          dimension={condition.dimension}
          fieldSource={fieldSource}
          setComparison={(comparison) => {
            setCondition({ ...condition, comparison });
          }}
        />
      );
    case WhereConditionDataType.TOKEN_HIERARCHY:
      return (
        <TokenHierarchyFilter
          comparison={condition.comparison}
          dataStructure={dataStructure}
          dimension={condition.dimension}
          fieldSource={fieldSource}
          setComparison={(comparison) => {
            setCondition({ ...condition, comparison });
          }}
        />
      );
    case WhereConditionDataType.NUMERIC:
      return (
        <NumericFilter
          comparison={condition.comparison}
          // Remount per field so an in-progress decimal can't outlive it.
          key={condition.dimension}
          setComparison={(comparison) => {
            setCondition({ ...condition, comparison });
          }}
          unit={field?.unit}
        />
      );
    case WhereConditionDataType.BOOLEAN:
      return (
        <BooleanFilter
          comparison={condition.comparison}
          setComparison={(comparison) => {
            setCondition({ ...condition, comparison });
          }}
        />
      );
    case WhereConditionDataType.DATE_FULL:
      return (
        <>
          <SegmentBuilderText>is</SegmentBuilderText>
          <FullDateTimeframeFilter
            comparison={condition.comparison}
            setComparison={(comparison) => {
              setCondition({ ...condition, comparison });
            }}
          />
        </>
      );
    case WhereConditionDataType.DATE_ANNUAL:
      return (
        <>
          <SegmentBuilderText>is</SegmentBuilderText>
          <AnnualDateTimeframeFilter
            comparison={condition.comparison}
            setComparison={(comparison) => {
              setCondition({ ...condition, comparison });
            }}
          />
        </>
      );
    case WhereConditionDataType.TOKEN:
      return (
        <TokenFilter
          comparison={condition.comparison}
          dataStructure={dataStructure}
          dimension={condition.dimension}
          fieldSource={fieldSource}
          setComparison={(comparison) => {
            setCondition({ ...condition, comparison });
          }}
        />
      );
    case WhereConditionDataType.CITY_PROXIMITY:
      return (
        <CityProximityFilter
          comparison={condition.comparison}
          dataStructure={dataStructure}
          dimension={condition.dimension}
          setComparison={(comparison) => {
            setCondition({ ...condition, comparison });
          }}
        />
      );
    case WhereConditionDataType.CUSTOM_FIELD:
      return (
        <CustomFieldFilter condition={condition} setCondition={setCondition} />
      );
    case WhereConditionDataType.STRING:
      return (
        <StringFilter
          comparison={condition.comparison}
          setComparison={(comparison) => {
            setCondition({ ...condition, comparison });
          }}
        />
      );
    default:
      assertNever(condition);
  }
});
